'use client'

import { Builder } from '@builder.io/react'

import { defaultTopNavProps } from './TopNav.defaults'
import { TopNavV4 } from './TopNavV4'

const stampHelperText = 'Optional stamp (ex: New! , Featured, Sale, etc.)'

const linkSubFields = [
  {
    name: 'title',
    type: 'text',
    localized: true,
  },
  {
    name: 'href',
    type: 'text',
    localized: true,
  },
  {
    name: 'newTab',
    friendlyName: 'Open in a new tab',
    type: 'boolean',
  },
]
const linkRichTextSubFields = [
  {
    name: 'title',
    type: 'richText',
    localized: true,
  },
  {
    name: 'href',
    type: 'text',
    localized: true,
  },
  {
    name: 'newTab',
    friendlyName: 'Open in a new tab',
    type: 'boolean',
  },
]

Builder.registerComponent(TopNavV4, {
  name: 'TopNavV4',
  friendlyName: 'TopNav V4',
  inputs: [
    {
      name: 'account',
      type: 'object',
      subFields: [
        {
          name: 'title', // logged in title
          type: 'string',
          friendlyName: 'Logged in title',
          defaultValue: defaultTopNavProps.account?.title,
        },
        {
          name: 'loggedOutTitle',
          type: 'string',
          defaultValue: defaultTopNavProps.account?.loggedOutTitle,
        },
        {
          name: 'loggedOutSubtitle',
          type: 'string',
          defaultValue: defaultTopNavProps.account?.loggedOutSubtitle,
        },
        {
          name: 'welcomeCopy',
          type: 'string',
          defaultValue: defaultTopNavProps.account?.welcomeCopy,
          helperText:
            'Displayed under "Account" for logged in users. If the user has a name set, it will be displayed as: "Welcome back, first_name last_name.". If no name is set or if name is "guest" it will displayed: "Welcome back!".',
        },
        {
          name: 'signOutCopy',
          type: 'string',
          defaultValue: defaultTopNavProps.account?.signOutCopy,
          helperText: 'Copy for the sign out button.',
        },
        {
          name: 'logInCopy',
          type: 'text',
          defaultValue: defaultTopNavProps.account?.logInCopy,
        },
        {
          name: 'links',
          type: 'list',
          subFields: [...linkSubFields],
          defaultValue: defaultTopNavProps.account?.links,
          helperText: 'List of links displayed when users are logged in or logged out.',
        },
        {
          name: 'bodiCta',
          type: 'object',
          subFields: [...linkSubFields],
          defaultValue: defaultTopNavProps.account?.bodiCta,
          helperText:
            'Displayed as a button next to the account icon. If the user is logged in and already has BODi, this will be hidden.',
        },
        {
          name: 'loggedInLinks',
          type: 'list',
          subFields: [
            ...linkSubFields,
            {
              name: 'imageUrl',
              type: 'file',
              allowedFileTypes: ['jpeg', 'png', 'webp'],
            },
            {
              name: 'imageAlt',
              type: 'text',
            },
            {
              name: 'subtitle',
              type: 'string',
            },
          ],
          defaultValue: defaultTopNavProps.account?.loggedInLinks,
        },
      ],
      defaultValue: defaultTopNavProps.account,
    },
    {
      name: 'logo',
      type: 'object',
      subFields: [
        {
          name: 'src',
          type: 'file',
          friendlyName: 'Logo image',
        },
        {
          name: 'href',
          type: 'string',
          friendlyName: 'Logo link',
        },
        {
          name: 'altText',
          type: 'string',
          friendlyName: 'Logo image alt text',
        },
        {
          name: 'desktopWidth',
          type: 'number',
        },
        {
          name: 'desktopHeight',
          type: 'number',
        },
        {
          name: 'mobileWidth',
          type: 'number',
        },
        {
          name: 'mobileHeight',
          type: 'number',
        },
      ],
      defaultValue: defaultTopNavProps.logo,
    },
    {
      name: 'items',
      friendlyName: 'Menu Items',
      type: 'list',
      subFields: [
        {
          name: 'title',
          type: 'string',
          localized: true,
        },
        {
          name: 'href',
          type: 'string',
          localized: true,
        },
        {
          name: 'newTab',
          friendlyName: 'Open in a new tab',
          type: 'boolean',
        },
        {
          name: 'stamp',
          type: 'string',
          helperText: stampHelperText,
          localized: true,
          defaultValue: 'NEW',
        },
        {
          name: 'hasSubItems',
          friendlyName: 'Has category items',
          type: 'boolean',
          defaultValue: true,
        },
        {
          name: 'subItems',
          friendlyName: 'Category Items',
          type: 'list',
          showIf: `options.get('hasSubItems') === true`,
          subFields: [
            ...linkSubFields,
            {
              name: 'links',
              type: 'list',
              subFields: [
                ...linkSubFields,
                {
                  name: 'stamp',
                  type: 'string',
                  localized: true,
                  helperText: stampHelperText,
                },
              ],
            },
          ],
        },
        {
          name: 'hasMiniMenu',
          type: 'boolean',
          defaultValue: false,
          helperText: 'Can only be used when category items are disabled.',
        },
        {
          name: 'miniMenuSubItems',
          friendlyName: 'Mini menu items',
          type: 'list',
          showIf: `options.get('hasMiniMenu') === true`,
          subFields: [...linkRichTextSubFields],
        },
        {
          name: 'tiles',
          type: 'list',
          subFields: [
            {
              name: 'image',
              type: 'file',
              allowedFileTypes: ['jpeg', 'png', 'webp'],
              localized: true,
              defaultValue: defaultTopNavProps.items?.[0]?.tiles?.[0]?.image,
            },
            {
              name: 'alt',
              type: 'text',
              localized: true,
              defaultValue: defaultTopNavProps.items?.[0]?.tiles?.[0]?.alt,
            },
            {
              name: 'href',
              type: 'text',
              localized: true,
              defaultValue: defaultTopNavProps.items?.[0]?.tiles?.[0]?.href,
            },
            {
              name: 'newTab',
              friendlyName: 'Open in a new tab',
              type: 'boolean',
              defaultValue: defaultTopNavProps.items?.[0]?.tiles?.[0]?.newTab,
            },
            {
              name: 'title',
              type: 'text',
              localized: true,
              defaultValue: defaultTopNavProps.items?.[0]?.tiles?.[0]?.title,
            },
            {
              name: 'stamp',
              friendlyName: 'Optional Stamp',
              type: 'text',
              localized: true,
              defaultValue: defaultTopNavProps.items?.[0]?.tiles?.[0]?.stamp,
            },
            {
              name: 'isVisible',
              type: 'boolean',
              localized: true,
              defaultValue: defaultTopNavProps.items?.[0]?.tiles?.[0]?.isVisible,
            },
          ],
          showIf: `options.get('hasSubItems') === true`,
          defaultValue: defaultTopNavProps.items?.[0]?.tiles,
        },
        {
          name: 'hasCtaMobile',
          type: 'boolean',
          showIf: `options.get('hasSubItems') === true`,
        },
        {
          name: 'ctaMobile',
          showIf: `options.get('hasCtaMobile') === true`,
          type: 'object',
          subFields: [...linkSubFields],
        },
        {
          name: 'ctaDesktop',
          type: 'object',
          showIf: `options.get('hasSubItems') === true`,
          subFields: [...linkSubFields],
        },
      ],
      defaultValue: defaultTopNavProps.items,
    },
    {
      name: 'mobileFooterLinks',
      type: 'list',
      subFields: [...linkSubFields],
      defaultValue: defaultTopNavProps.mobileFooterLinks,
    },
  ],
  image: 'https://unpkg.com/@tabler/icons@2.44.0/icons/layout-navbar.svg',
})
